/**
 * A notice over the page: something the player must take in before the game
 * goes on — the connection gone for good, the session over, a moderator's
 * word, the full screen a session asks for. The notice's sheet (NoticeSheet:
 * its anatomy, its tones, its fact and its clock) on the Modal shell; what
 * the Modal adds is said here.
 *
 * It has no close button and takes no Escape: it waits on its actions, and
 * Escape over it closes nothing under it either. Like every dialog, the page
 * behind it is inert, so the one-gold rule counts its action alone. It sits on
 * the notification rung, over any dialog and any moment, and renders beside
 * the dialogs rather than in the page, so it stays live over one; a moment
 * that arrives while it is up is drawn under it, and takes neither the focus
 * nor the keys from it (use-modal-behavior.ts). Its title names the dialog
 * and its sentence describes it; `alertdialog` for a notice that interrupts.
 *
 *   <NoticeDialog open={lost} tone="danger" glyph={GLYPH.warning}
 *                 title="Connection lost" description="You have been disconnected…"
 *                 actions={<Button tone="primary" onClick={reconnect}>Reconnect</Button>} />
 */

import { useId } from 'react';

import { Modal } from './modal';
import { NoticeSheet, type NoticeSheetProps } from './notice-sheet';

/** The sheet's own say, opened over the page: what it names, folds and ends on is a page's. */
type NoticeDialogProps = Omit<NoticeSheetProps, 'kicker' | 'details' | 'foot' | 'role' | 'titleId' | 'descriptionId'> & {
  open: boolean;
  /** `alertdialog` for a notice that interrupts: the connection lost, a removal, an invitation that acts by itself. */
  role?: 'dialog' | 'alertdialog';
};

export function NoticeDialog({ open, role = 'dialog', ...sheet }: NoticeDialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  return (
    <Modal open={open} role={role} labelledBy={titleId} describedBy={descriptionId} layer="notice">
      <NoticeSheet {...sheet} titleId={titleId} descriptionId={descriptionId} />
    </Modal>
  );
}
